import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CorporateContactType } from "@/lib/validations/schema";
import { ColumnDef } from "@tanstack/react-table";
import { AxiosRequestConfig } from "axios";
import { company_contact_status } from "common/filters";
import { Spinner } from "components";
import { DataTableColumnHeader } from "components/Table/data-table-column-header";
import { DataTable } from "components/Table/DataTable";
import { Search, X } from "lucide-react";
import NewCustomersPage from "pages/Customers/newcustomer";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import { getCompanyLeads, GetCompanyRequestType } from "service/api";
import {
  getSiblingBranch,
  GetSiblingBranchRequestType,
} from "service/apis/BranchDetailsApi";
import {
  sendDailyRates,
  SendDailyRatesReqType,
} from "service/apis/Opportunities";
import { Toaster } from "sonner";
import { IPage } from "./partner_contact";

const allcolumns: ColumnDef<CorporateContactType>[] = [
  {
    id: "id",
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    cell: ({ row }) => <div className="">{row.getValue("id")}</div>,
    enableHiding: false,
    enableSorting: false,
    filterFn: "includesString",
  },
  {
    id: "name",
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Company Name" />
    ),
    filterFn: "includesString",
    enableSorting: false,
  },
  {
    id: "type",
    accessorKey: "type",
    header: ({ column }) => (
      <DataTableColumnHeader
        className="text-center"
        column={column}
        title="Type"
      />
    ),
    cell: ({ row }) => {
      return <p className=" text-center w-full">{row.getValue("type")}</p>;
    },
    enableSorting: false,
  },
  {
    id: "location",
    accessorKey: "location",
    header: ({ column }) => (
      <DataTableColumnHeader
        className="text-center"
        column={column}
        title="Location"
      />
    ),
    cell: ({ row }) => {
      return (
        <p className=" text-center w-full">
          {row.getValue("location") || "N/A"},{row.original.state || "N/A"}
        </p>
      );
    },
    enableSorting: false,
  },
  {
    id: "assignedTo",
    accessorKey: "assignedTo",
    header: ({ column }) => (
      <DataTableColumnHeader
        className="text-center"
        column={column}
        title="Assigned To"
      />
    ),
    cell: ({ row }) => {
      return (
        <p className=" text-center w-full">{row.getValue("assignedTo")}</p>
      );
    },
    enableSorting: false,
  },
  {
    id: "lead_status",
    accessorKey: "lead_status",
    header: ({ column }) => (
      <DataTableColumnHeader
        className="text-center"
        column={column}
        title="Status"
      />
    ),
    cell: ({ row }) => {
      const partnerStatus = row.original.status;
      const leadStatus = row.getValue("lead_status");

      const statusToUse =
        partnerStatus === "Active"
          ? partnerStatus
          : leadStatus || partnerStatus;

      const status = company_contact_status.find(
        (status) => status.value === statusToUse
      );

      if (!status) {
        return null;
      }

      return (
        <p
          style={{ backgroundColor: status.bgColor, color: status.textColor }}
          className="py-[3px] text-sm leading-4 text-center font-medium max-w-[160px]  px-2 mx-auto rounded-md"
        >
          {status.label}
        </p>
      );
    },
    enableSorting: false,
  },
];

const tabData = [
  { label: "All", value: "all", enabled: true },
  { label: "Leads", value: "leads", enabled: true },
  { label: "Clients", value: "clients", enabled: true },
];

export default function CorporateContact() {
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState<CorporateContactType[]>([]);
  const [activeTab, setActiveTab] = useState("all");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSendingRates, setIsSendingRates] = useState<Boolean>(false);
  const [pageDetails, setPageDetails] = useState<IPage>();
  const [staffs, setStaffs] = useState<any[]>([]);
  const navigate = useNavigate();
  const [selectedBranch, setSelectedBranch] = useState<any>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const defaultCurrencies = ["USD", "EUR", "GBP", "SGD", "AED", "THB"];
  const [currencyRows, setCurrencyRows] = useState(
    defaultCurrencies?.map((ccy) => ({
      ccy,
      buy: 0,
      sell: 0,
      card: 0,
    }))
  );
  // get all staff details
  const getBranch = () => {
    const auth: string | null = localStorage.getItem("authToken");
    if (auth === null) return null;
    const authToken = JSON.parse(auth);

    const payload: AxiosRequestConfig<GetSiblingBranchRequestType> = {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    };

    setLoading(true);

    getSiblingBranch(payload)
      .then((res) => {
        console.log(res?.data);
        setLoading(false);
        setStaffs(
          res?.data?.map((result: any) => {
            return {
              value: result.id,
              label: result?.name,
            };
          })
        );
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err.response.data.message);
      });
  };

  useEffect(() => {
    if (activeTab === "leads") {
      getBranch();
    }
  }, [activeTab]);

  useEffect(() => {
    const savedBranch = localStorage.getItem("branch");
    if (savedBranch) {
      const matchedBranch = staffs?.find(
        (s) => String(s.value) === String(savedBranch)
      );
      if (matchedBranch) setSelectedBranch(matchedBranch);
    }
  }, [activeTab, staffs]);

  const handleChange = (index, field, value) => {
    const updatedRows = [...currencyRows];
    updatedRows[index][field] =
      field === "ccy" ? value : parseFloat(value) || 0;
    setCurrencyRows(updatedRows);
  };

  const handleRemoveRow = (index) => {
    if (index >= defaultCurrencies.length) {
      setCurrencyRows(currencyRows.filter((_, i) => i !== index));
    }
  };

  const handleAddRow = () => {
    setCurrencyRows([...currencyRows, { ccy: "", buy: 0, sell: 0, card: 0 }]);
  };
  const sendRatelist = async () => {
    if (!selectedBranch) {
      toast.error("Please select a branch");
      return;
    }
    setLoading(true);
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);
    const payload: AxiosRequestConfig<SendDailyRatesReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
      data: {
        branch: selectedBranch?.value,
        rates_list: currencyRows?.map((row: any) => ({
          Currency: row.ccy,
          buy_rate: parseFloat(row.buy) || 0,
          sell_rate: parseFloat(row.sell) || 0,
          card_we_sell: parseFloat(row.card) || 0,
        })),
      },
    };
    try {
      const res = (await sendDailyRates(payload)) as any;
      if (res.status === 200) {
        toast.success("Daily rates sent successfully");
        setLoading(false);
        setShowConfirmation(false);
        setCurrencyRows(
          defaultCurrencies.map((ccy) => ({
            ccy,
            buy: 0,
            sell: 0,
            card: 0,
          }))
        );
        setSelectedBranch(null);
      }
    } catch (error) {
      toast.error("Failed to send daily rates");
      setLoading(false);
    }
  };

  const onRowClick = (row: any) => {
    if (row?.partnerContactStatus === "lead") {
    navigate(`/leads/corporate/${row?.coorporate_company_id}`);
    } 
  };

  const columns = useMemo(() => {
    return allcolumns.filter((column) => {
      if (activeTab === "all") {
        return !["contactStatus"].includes(column?.id as string);
      }
      return allcolumns;
    });
  }, [activeTab]);

  const fetchResults = (
    page: number,
    per_page: number,
    searchQuery: string,
    category: string
  ) =>
    // searchTerm: string,
    // page: number | string,
    // perPage: string | number,
    // type: string,
    // status: string,
    {
      setLoading(true);

      // get auth token from localstorage
      const auth: string | null = localStorage.getItem("authToken");
      if (auth === null) return null;
      const authToken = JSON.parse(auth);

      const payload: AxiosRequestConfig<GetCompanyRequestType> = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        params: {
          category: category,
          q: searchQuery,
          paging: {
            page: page,
            per_page: per_page,
            offset: 0,
          },
        },
      };
      // if (activeTab === "clients") {
      //   payload.params = { isActive: true };
      // }
      getCompanyLeads(payload)
        .then((response) => {
          console.log(response);
          setLoading(false);
          setCompany(() => {
            return (
              response?.data?.map(
                (
                  result: Partial<{
                    id: number;
                    company_name: string;
                    address: string | null;
                    email: string;
                    phone: string | null;
                    contacts_id: number[];
                    rm_id: number;
                    status: string;
                    type: string;
                    lead_status: string;
                    state: string;
                    extra_detail: {
                      channel_of_interaction_id: number;
                      service: string[];
                      meeting_staff: string[];
                      _channel_of_interaction: string[];
                    };
                    rm_staff: {
                      id: number;
                      fname: string;
                    };
                    coorporate_company_id: number;
                  }>
                ) => {
                    // Find the last address object with a valid City or State
                    const validAddress = result?.address_id?.slice().reverse().find(
                            (addr: { City: any; State: any; }) => addr?.City || addr?.State
                    );
                  return {
                    id: result?.coorporate_company_id,
                    name: result?.entity_name,
                    location: validAddress?.City || "N/A",
                    state: validAddress?.State || "N/A",
                    assignedTo: result?.relation_manager?.fname || "N/A",
                    type: result?.type || "N/A",
                    partnerContactStatus: result?.status || "N/A",
                    status: result?.status || "N/A",
                    lead_status: result?.lead_status || "N/A",
                    staffName: "",
                    subType: "",
                    coorporate_company_id: result?.coorporate_company_id,
                  };
                }
              ) ?? []
            );
          });
          setPageDetails({
            pageTotal: response?.data?.pageTotal,
            curPage: response?.data?.curPage,
            itemsReceived: response?.data?.itemsReceived,
            itemsTotal: response?.data?.itemsTotal,
            nextPage: response?.data?.nextPage,
            offset: response?.data?.offset,
            perPage: response?.data?.perPage,
            prevPage: response?.data?.prevPage,
          });
        })
        .catch(() => {
          setLoading(false);
        });
    };
  useEffect(() => {
    fetchResults(page, perPage, searchQuery, activeTab);
  }, [statusFilter, activeTab, page, perPage, searchQuery]);

  return (
    <>
      {loading && <Spinner />}
      <div className="flex h-full gap-10 py-5 px-6 sm:px-0 w-full flex-col overflow-hidden">
        <div className="flex gap-5 justify-between w-full items-center">
          <Tabs
            className="bg-navbar-menu-bg w-fit flex rounded-lg gap-2"
            value={activeTab}
            onValueChange={(val) => {
              if (val === "partners") {
                setStatusFilter("active");
              } else {
                setStatusFilter("");
              }
              setActiveTab(val);
            }}
          >
            <TabsList className="flex flex-wrap sm:flex-nowrap w-full h-full">
              {tabData
                .filter((tab) => true)
                .map((tab, index: number) => (
                  <TabsTrigger
                    key={index}
                    value={tab.value}
                    disabled={!tab.enabled}
                    className={`py-3 px-10 sm:px-4 disabled:opacity-40 text-sm h-[44px] min-w-[120px] sm:min-w-[100px] font-semibold ${
                      tab.value === activeTab
                        ? "border border-blue-700 bg-white-A700 text-blue-700 rounded-lg"
                        : " text-black-label"
                    }`}
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
            </TabsList>
          </Tabs>
         
        </div>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="relative flex items-center w-full max-w-[470px]">
            <Input
              placeholder="Search by corporate name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="py-3 pl-10 pr-4 flex gap-3.5 rounded-[30px] w-full"
            />
            <Search className="absolute left-3 h-4 w-4 text-gray-500" />
          </div>

          {activeTab === "leads" && (
            <button
              className="rounded-lg py-[11px] px-4 border bg-blue-700 text-[#fff] font-semibold text-sm"
              onClick={() => setIsSendingRates(true)}
            >
              Send Daily Rates
            </button>
          )}
           {activeTab === "all" ? (
            <Link to="/lead/createcontact/corporate" className="sm:hidden">
              <button className="rounded-lg py-[11px] px-4 border bg-blue-700 text-white-A700 font-semibold text-sm">
                New Corporate Lead
              </button>
            </Link>
          ) : activeTab !== "leads" ? (
            <Link to="/corporateregistration" className="sm:hidden">
              <button className="rounded-lg py-[11px]  px-4 border bg-blue-700 text-white-A700 font-semibold text-sm">
                Register Corporate
              </button>
            </Link>
          ) : null}
          <Dialog open={isSendingRates} onOpenChange={setIsSendingRates}>
            <DialogOverlay className="fixed inset-0 bg-black/60" />
            <DialogContent className="max-w-4xl sm:max-w-2xl w-full p-0 bg-[#fff] rounded-lg shadow-lg ">
              <div className="relative w-full p-8">
                {/* <DialogClose
                  className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
                  onClick={() => setIsSendingRates(false)}
                >
                  <X size={20} />
                </DialogClose> */}

                {/* Header */}
                <DialogHeader className="mb-4 border-b border-gray-200 pb-2">
                  <DialogTitle className="text-lg font-semibold text-gray-800 text-left">
                    Daily Rate
                  </DialogTitle>
                </DialogHeader>

                {/* Date */}
                <div className="mb-4 flex flex-row justify-between items-center">
                  <label className="block text-sm font-medium text-[#666666]">
                    Date
                  </label>
                  <div className="mt-1 text-sm text-[#33333] bg-gray-100 font-normal rounded-md px-3 py-2 inline-block">
                    {new Date().toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </div>
                </div>

                {/* Branch Selector */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#666666]">
                    Select Branch
                  </label>
                  <Select
                    className="mt-1"
                    options={staffs}
                    value={selectedBranch}
                    onChange={setSelectedBranch}
                    placeholder="Select Branch"
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        borderRadius: 8,
                        borderColor: '#999999',
                        boxShadow: state.isFocused ? '0 0 0 1px #999999' : 'none',
                        '&:hover': {
                          borderColor: '#999999',
                        },
                        minHeight: '40px',
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: '#666',
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#333333',
                        fontSize: '14px',
                        fontWeight: '400',
                      }),
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {staffs
                      ? `This branch consist of ${staffs?.length} corporates`
                      : "No branch selected"} 
                  </p>
                </div>

                {/* Rate Table */}
                <div className="border border-gray-200 rounded-[8px] overflow-hidden">
                  {/* Header Row */}
                  <div className="grid grid-cols-4 bg-[#0052CC] text-[#fff] text-sm font-medium px-4 py-2">
                    <div>CCY</div>
                    <div>Currency we buy</div>
                    <div>Currency we sell</div>
                    <div>Card we sell (Travel card)</div>
                  </div>

                  {/* Dynamic Rows */}
                  {currencyRows.map((row, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-4 gap-3 px-4 py-3 bg-gray-50 border-t border-gray-100 items-center relative"
                    >
                      <input
                        type="text"
                        value={row.ccy}
                        onChange={(e) =>
                          handleChange(index, "ccy", e.target.value)
                        }
                        className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white"
                        placeholder="USD"
                        disabled={index < defaultCurrencies?.length}
                      />
                      <input
                        type="number"
                        value={row.buy}
                        onChange={(e) =>
                          handleChange(index, "buy", e.target.value)
                        }
                        className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white"
                        placeholder="Buy Rate"
                      />
                      <input
                        type="number"
                        value={row.sell}
                        onChange={(e) =>
                          handleChange(index, "sell", e.target.value)
                        }
                        className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white"
                        placeholder="Sell Rate"
                      />
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={row.card}
                          onChange={(e) =>
                            handleChange(index, "card", e.target.value)
                          }
                          className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white w-full"
                          placeholder="Card Rate"
                        />
                        {index >= defaultCurrencies.length && (
                          <button
                            onClick={() => handleRemoveRow(index)}
                            className="text-red-500 text-xs whitespace-nowrap"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add Row Link */}
                <div className="mt-3">
                  <button
                    onClick={handleAddRow}
                    className="text-sm text-[#165FE3] font-medium flex items-center gap-1"
                  >
                    <span className="w-[17px] h-[17px] rounded-full text-[#165FE3] border border-[#165FE3] text-white flex items-center justify-center text-[13px] leading-none">
                      +
                    </span>
                    Add Row
                  </button>
                </div>

                {/* Footer Buttons */}
                <DialogFooter className="mt-6 flex bg-[#F9F9F9]  flex-row justify-start gap-2 border-t pt-4">
                  <button
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsSendingRates(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 bg-[#0052CC] text-[#fff] rounded-md text-sm hover:bg-blue-800"
                    onClick={() => setShowConfirmation(true)}
                    disabled={!selectedBranch || currencyRows.length === 0}
                  >
                    Send Email
                  </button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>

          {/* confirmation dialog */}
          <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
            <DialogOverlay className="fixed inset-0 bg-black/40" />
            <DialogContent className="max-w-[436px] sm:max-w-xl w-full px-10 py-8 bg-[#fff] rounded-lg shadow-lg">
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold text-[#333333]">
                  Do you want to send email to {currencyRows.length} corporates?
                </DialogTitle>
                {/* <DialogDescription className="mt-2 font-medium text-base text-[#7E7E7E]">
                  You're about to send emails to {currencyRows.length} corporate
                  contacts. Confirm to send the email.
                </DialogDescription> */}
              </DialogHeader>

              <div className="relative mt-6 flex justify-center p-3 gap-5">
                <button
                  className="px-4 py-2.5 border border-[#165FE3] text-sm text-[#165FE3] rounded-md hover:bg-gray-100"
                  onClick={() => setShowConfirmation(false)}
                >
                  No
                </button>
                <button
                  className="px-4 py-2.5 bg-[#165FE3] text-[#fff] rounded-[8px] text-sm hover:bg-blue-800"
                  onClick={() => {
                    setShowConfirmation(false);
                    setIsSendingRates(false);
                    sendRatelist();
                  }}
                >
                  Yes
                </button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex-1 relative overflow-auto">
          {loading && <Spinner />}
          {!activeTab ? (
            <NewCustomersPage />
          ) : (
            <DataTable
              columns={columns}
              data={company}
              onRowClick={(row) => onRowClick(row)}
              isToolbarVisible={true}
              isPaginationVisible={true}
              isPageCountVisible={false}
              isToolbarVisible={false}
              defaultSorting={[{ id: "id", desc: true }]}
              onClickNextPage={() => setPage((page) => page + 1)}
              onClickPreviousPage={() => setPage((page) => page - 1)}
              totalPages={pageDetails?.pageTotal}
              currentPage={pageDetails?.curPage}
              canNextPage={!!pageDetails?.nextPage}
              canPreviousPage={!!pageDetails?.prevPage}
            />
          )}
        </div>
        {activeTab === "all" ? (
          <Link
            className="p-2.5 rounded-[20px] hidden sm:flex text-center border border-blue-700"
            to={"/lead/createcontact"}
          >
            <button className="rounded-lg py-[11px] px-4 border w-full bg-blue-700 text-white-A700 font-semibold text-sm">
              New Corporate Lead
            </button>
          </Link>
        ) : (
          <Link
            className="p-2.5 rounded-[20px] hidden sm:flex text-center border border-blue-700"
            to={"/corporateregistration"}
          >
            <button className="rounded-lg py-[11px] px-4 border w-full bg-blue-700 text-white-A700 font-semibold text-sm">
              Register Corporate
            </button>
          </Link>
        )}
        <Toaster position="top-right" />
      </div>
    </>
  );
}
